export type CreatorScope =
  | 'widget'
  | 'activity'
  | 'program'
  | 'programSchema'
  | 'initiative'
  | 'comms';

/**
 * Mode = the configuration knob that selects which cell of the 3x3
 * program-delivery matrix a program operates in.
 *
 *   fast     → top-left cell (Chat × API). Stripped chrome, hardcoded sensible
 *              defaults. The "anyone with AI" wedge — describe → 60s → run.
 *   standard → center cell (Chat/UI × API/Platform). Current default.
 *              Full Shell chrome, full settings menu.
 *   pro      → center cell with all advanced settings exposed.
 *   agent    → headless / MCP transport. Not a UI mode — set when a request
 *              comes via MCP rather than the web Shell.
 *
 * @see /Users/dericklee/.claude/plans/delightful-chasing-tarjan.md
 */
export type AiShellMode = 'fast' | 'standard' | 'pro' | 'agent';

/**
 * Pre-filled AI Co-Pilot configuration the creator can edit before generation.
 *
 * When set (e.g., by Fast mode's setMode preset), the AIPC backend recognizes
 * the persona/channels questions are already answered and skips the
 * AUTONOMOUS PROGRAM MANAGEMENT block in the interview, jumping straight to
 * the program spec. Standard / Pro modes leave this null so the interview
 * asks the questions normally.
 *
 * This is the single source of truth for "what does Fast mode pre-fill?"
 * — defined here, consumed by both the frontend (visible defaults) AND the
 * backend (skip questions when set).
 */
export interface PrefilledAiConfig {
  personaSource: 'self' | 'universal' | 'custom';
  personaName: string | null; // null = use Auth0 user's display name
  personaBio: string | null;
  personaTone: string | null;
  personaSignatureStyle: string | null;
  selectedUniversalPersonaId: string | null;
  channels: ('sms' | 'email')[];
  deadlineBehavior: 'proactive' | 'reactive' | 'off';
}

export interface AiShellSettings {
  // Posture knob: which cell of the 3x3 matrix are we in?
  mode: AiShellMode;
  targetAudience: string | null;
  duration: string | null;
  saveToLibrary: boolean;
  // Program creation settings — controls how AI generates programs
  widgetStrategy: 'custom' | 'standard'; // 'custom' = 1 rich custom widget per activity
  alwaysNewWidgets: boolean; // true = generate fresh widgets from scratch
  // Resource extraction settings
  resourceExtraction: 'during-interview' | 'after-approval' | 'after-creation';
  maxResources: number; // Soft cap — default 20
  // WS8 — how a resource (esp. a foundational video) maps to activities:
  //  'foundational-one' (default): foundational video → one activity, others free
  //  'pure-ai': honor the AI's targetActivities as-is
  //  'everything-single-homed': every resource only in its target activity(ies)
  resourceTargetingMode: 'foundational-one' | 'pure-ai' | 'everything-single-homed';
  // WS8 — multiple videos: 'ai-decides' (separate vs sequence) | force-separate | force-sequence
  multiVideoHandling: 'ai-decides' | 'force-separate' | 'force-sequence';
  // Born-Intelligent AI Co-Pilot settings
  aiCoPilotEnabled: boolean;
  participationCeiling: 'manual' | 'assisted' | 'collaborative' | 'autonomous';
  engagementStyle: 'proactive' | 'responsive' | 'minimal';
  /**
   * Pre-filled aiConfig — when non-null, the interview skips the persona
   * questions block and uses these values directly. Set by Fast mode.
   * null in Standard / Pro modes (interview asks the questions).
   */
  prefilledAiConfig: PrefilledAiConfig | null;
}

export interface AiShellAttachment {
  type: 'activitySchema' | 'programSchema' | 'program';
  id: string;
  name: string;
}

export interface AiShellActivityFlags {
  isTeamAdk: boolean;
  isSkippable: boolean;
  setupRequired: boolean;
}

export type AiShellViewMode = 'build' | 'participate';
export type AiShellPreviewRole = 'student' | 'employer' | 'organizer';
export type AiShellDataSourceMode = 'schema' | 'program' | 'template' | 'student' | null;

export interface AiShellWidgetSummary {
  totalWidgets: number;
  studentVisible: number;
  organizerOnly: number;
  presetFieldsCount: number;
}

export type StreamingActivityStatus = 'pending' | 'building' | 'compiling' | 'done' | 'error';
export type CreationPhase = 'idle' | 'scaffolding' | 'building' | 'complete';

export interface StreamingActivityMeta {
  name: string;
  category: string;
  icon: string;
  color: string;
}

export interface AiShellStreamingCreation {
  isStreaming: boolean;
  programId: string | null;
  currentActivityIndex: number;
  totalActivities: number;
  activityStatuses: StreamingActivityStatus[];
  activityNames: string[];
  activityMeta: StreamingActivityMeta[];
  categoryMeta: Array<{ name: string; icon: string }>;
  creationPhase: CreationPhase;
}

export interface UiState {
  aiShellOpen: boolean;
  aiShellScope: CreatorScope;
  aiShellEntityId: string | null;
  aiShellEntityName: string | null;
  aiShellEntityDesc: string | null;
  aiShellEntityIcon: string | null;
  aiShellEntityColor: string | null;
  aiShellContext: Record<string, unknown> | null;
  aiShellSettings: AiShellSettings;
  aiShellAttachments: AiShellAttachment[];
  aiShellActivityFlags: AiShellActivityFlags;
  aiShellPendingGeneration: Record<string, unknown> | null;
  aiShellHasUnsavedChanges: boolean;
  aiShellSaveRequested: boolean;
  aiShellViewMode: AiShellViewMode;
  aiShellPreviewRole: AiShellPreviewRole;
  aiShellDataSourceMode: AiShellDataSourceMode;
  aiShellWidgetSummary: AiShellWidgetSummary | null;
  aiShellProgramEditApplied: boolean;
  aiShellStreamingCreation: AiShellStreamingCreation;
  aiShellInitialPrompt: string | null;
}

const state: UiState = {
  aiShellOpen: false,
  aiShellScope: 'program',
  aiShellEntityId: null,
  aiShellEntityName: null,
  aiShellEntityDesc: null,
  aiShellEntityIcon: null,
  aiShellEntityColor: null,
  aiShellContext: null,
  aiShellSettings: {
    // Default mode is now 'fast' — /create lands in the wedge experience by default.
    // Users can switch to Standard / Pro via the Mode dropdown in Shell Settings.
    // Note: prefilledAiConfig stays null at boot; useAiShellMode.setMode('fast')
    // is what populates the persona/channel defaults — that fires on first
    // explicit mode-set OR on Shell mount when mode is already 'fast' (see
    // AiCreatorShell setup hook).
    mode: 'fast',
    targetAudience: null,
    duration: null,
    saveToLibrary: false,
    widgetStrategy: 'custom',
    alwaysNewWidgets: true,
    resourceExtraction: 'during-interview',
    maxResources: 20,
    resourceTargetingMode: 'foundational-one',
    multiVideoHandling: 'ai-decides',
    aiCoPilotEnabled: true,
    participationCeiling: 'collaborative',
    engagementStyle: 'responsive',
    prefilledAiConfig: null
  },
  aiShellAttachments: [],
  aiShellActivityFlags: {
    isTeamAdk: false,
    isSkippable: false,
    setupRequired: false
  },
  aiShellPendingGeneration: null,
  aiShellHasUnsavedChanges: false,
  aiShellSaveRequested: false,
  aiShellViewMode: 'build',
  aiShellPreviewRole: 'student',
  aiShellDataSourceMode: null,
  aiShellWidgetSummary: null,
  aiShellProgramEditApplied: false,
  aiShellStreamingCreation: {
    isStreaming: false,
    programId: null,
    currentActivityIndex: -1,
    totalActivities: 0,
    activityStatuses: [],
    activityNames: [],
    activityMeta: [],
    categoryMeta: [],
    creationPhase: 'idle'
  },
  aiShellInitialPrompt: null
};

export default state;
